import { z } from 'zod'
import { Model } from '../../_kernel/architecture'

import { Availability } from './Availability'
import { ID } from '../../_kernel/ID'

import type { GETAvailabilityPropertyIdResponseType } from '../infrastructure/HTTPBookingRepository/schemas/v4/availability/property/:id'
import { Board } from './Board'
import { BoardCode } from './BoardCode'
import { Name } from '../../_kernel/Name'
import { BoardGroup } from './BoardGroup'
import { PaymentPolicy } from './PaymentPolicy'
import { Rate } from './Rate'
import { CancellationPolicy } from './CancellationPolicy'
import { DateModel } from '../../_kernel/Date'
import { Room } from './Room'
import { ListOfBoard } from './ListOfBoard'
import { Price } from '../../_kernel/Price'
import { Money } from '../../_kernel/Money'
import type { Currencies } from '../../_kernel/Currency'
import { Currency } from '../../_kernel/Currency'
import type { Market } from '../../shared/domain/Market'
import type { Affiliate } from '../../shared/domain/Affiliate'
import type { CriteriaAvailability } from './CriteriaAvailability'
import { DateRange } from './DateRange'
import { Pax } from '../../shared/domain/Pax'
import { ListOfFacility } from '../../shared/domain/ListOfFacility'
import { ListOfImage } from '../../_kernel/ListOfImage'
import { Allotment } from './Allotment'
import { ListOfRate } from './ListOfRate'

export const ListOfAvailabilityValidation = z.object({
  list: z.instanceof(Availability, { message: '[ListOfAvailability] list is required' }).array(),
  dateRange: z.instanceof(DateRange, { message: '[ListOfAvailability] dateRange is required' }),
})

export class ListOfAvailability extends Model {
  static create({ list, dateRange }: z.infer<typeof ListOfAvailabilityValidation>) {
    ListOfAvailabilityValidation.parse({ list, dateRange })
    return new ListOfAvailability(list, dateRange, false)
  }

  static empty() {
    return new ListOfAvailability([], DateRange.default(), true)
  }

  static fromAvailabilityApi({
    json,
    checkin,
    checkout,
    market,
    affiliate,
  }: {
    json: GETAvailabilityPropertyIdResponseType
    market: Market
    affiliate: Affiliate
    checkin: string
    checkout: string
  }) {
    const { defaults: _defaults, room_content: _roomContent, ...availabilityProperties } = json
    return ListOfAvailability.create({
      list: Object.values(availabilityProperties)
        .map(property => property.availability)
        .flat()
        .map(availabilityInput => {
          const rate = Rate.create({
            paymentPolicy: PaymentPolicy.create({
              value: availabilityInput.payment_policy,
            }),
            ...(availabilityInput.cancellation_policies?.[0] && {
              cancellationPolicy: CancellationPolicy.create({
                fromDate: DateModel.create({
                  value: availabilityInput.cancellation_policies[0].from,
                }),
                price: Money.create({
                  amount: availabilityInput.cancellation_policies[0].amount,
                  currency: Currency.create({
                    value: availabilityInput.cancellation_policies[0].currency as Currencies,
                  }),
                }),
              }),
            }),
            ...(availabilityInput.rate_type && {
              rateType: availabilityInput.rate_type,
            }),
          })

          return Availability.create({
            id: ID.create({ value: availabilityInput.id_availability }),
            board: Board.create({
              code: BoardCode.create({ value: availabilityInput.board_code }),
              name: Name.create({ value: availabilityInput.board_name }),
              group: BoardGroup.create({
                name: availabilityInput.board_group_name,
                code: availabilityInput.board_group_code,
              }),
            }),
            rate,
            price: Price.fromAvailabilityApi({
              json: availabilityInput,
              market,
              affiliate,
            }),
            room: Room.create({
              id: ID.create({ value: availabilityInput.room_unique_id }),
              name: Name.create({ value: availabilityInput.room_name }),
              pax: Pax.fromAvailabilityApi({
                adults: availabilityInput.adults,
                children: availabilityInput.children_ages,
              }),
              allotment: Allotment.create({ value: availabilityInput.allotment }),
              facilities: ListOfFacility.fromAvailabilityApi({
                availability: availabilityInput,
                json,
              }),
              images: ListOfImage.fromAvailabilityApi({ availability: availabilityInput, json }),
            }),
          })
        }),
      dateRange: DateRange.fromAvailabilityApi({ from: checkin, to: checkout }),
    })
  }

  constructor(
    private readonly _list: Availability[],
    private readonly _dateRange: DateRange,
    private readonly _empty?: boolean
  ) {
    super()
  }

  getListOfBoardByRoomID({ roomID }: { roomID: ID }) {
    return this.getListOfAvailabilityByRoomID({ id: roomID }).boards()
  }

  getListOfRatesByRoomID({ roomID }: { roomID: ID }) {
    return this.getListOfAvailabilityByRoomID({ id: roomID }).rates()
  }

  getRooms() {
    return this._list.map(item => item.roomID())
  }

  toJSON() {
    return { list: this._list.map(item => item.toJSON()) }
  }

  isEmpty() {
    return this._empty
  }

  map<T>(callback: (availability: Availability, index: number) => T) {
    return this._list.map((availability, index) => callback(availability, index))
  }

  at(index: number) {
    return this._list.at(index)
  }

  findAvailabilityByCriteria({ criteria }: { criteria: CriteriaAvailability }) {
    return this._list.find(item => criteria.equals(item))
  }

  getNights() {
    return this._dateRange.getNights()
  }

  private getListOfAvailabilityByRoomID({ id }: { id: ID }) {
    const list = this._list.filter(item => item.roomID().equals(id))
    return ListOfAvailability.create({ list, dateRange: this._dateRange })
  }

  private boards() {
    return ListOfBoard.create({ list: this._list.map(item => item.board()) })
  }

  private rates() {
    return ListOfRate.create({ list: this._list.map(item => item.rate()) })
  }
}
